import { motion } from 'framer-motion';
import { useMemo, useState, useEffect } from 'react';
import { useThemeStore } from '../store/themeStore';
import './AnimatedLogo.css';

export function AnimatedLogo() {
  const { currentTheme } = useThemeStore();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate theme-aware rainbow colors (same as AnimatedRainbowBorder)
  // On mobile, use white as base with subtle color hints for better visibility
  const gradientColors = useMemo(() => {
    if (isMobile) {
      // Pure white on mobile - no animation needed
      return [
        '#ffffff',
        '#ffffff',
        '#ffffff',
      ];
    }
    const colors = currentTheme.colors;
    return [
      colors.accent,
      colors.info,
      colors.success,
      colors.warning,
      colors.error,
      colors.ema9,
      colors.ema21,
      colors.accent, // Loop back for seamless animation
    ];
  }, [currentTheme, isMobile]);

  // Create the gradient string
  const gradient = useMemo(() => {
    const colorStops = gradientColors.map((color, i) => {
      const percent = (i / (gradientColors.length - 1)) * 100;
      return `${color} ${percent}%`;
    });
    return `linear-gradient(90deg, ${colorStops.join(', ')})`;
  }, [gradientColors]);

  // On mobile, use typewriter animation
  if (isMobile) {
    const text = "Ultimate Trading Terminal";
    return (
      <span className="animated-logo-text typewriter">
        {text.split('').map((char, i) => (
          <motion.span
            key={i}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.05,
              delay: i * 0.05,
            }}
          >
            {char}
          </motion.span>
        ))}
        <motion.span
          className="typewriter-cursor"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            duration: 0.8,
            delay: text.length * 0.05,
            repeat: Infinity,
          }}
        >
          |
        </motion.span>
      </span>
    );
  }

  return (
    <motion.span
      key={currentTheme.id} // Force remount on theme change to restart animation
      className="animated-logo-text rainbow"
      style={{
        background: gradient,
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
      }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
      }}
      transition={{
        opacity: { duration: 0.8 },
        backgroundPosition: {
          duration: 8,
          ease: 'linear',
          repeat: Infinity,
        },
      }}
    >
      Ultimate Trading Terminal
    </motion.span>
  );
}
